import { Button } from "@/components/ui/button";
import Icons from "@/components/Icons";
import { Loader2Icon } from "lucide-react";
import type { MouseEventHandler } from "react";
import React, { useState, useRef, useEffect } from "react";
import type { IconType } from "@/types";
import type { ThemeConfig } from "@/lib/theme-utils";

interface SavedCard {
  id: string;
  card: {
    brand: string;
    last4: string;
  };
}

interface SavedCardButtonProps {
  cardBrand: string;
  cardLast4: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  // New props for dropdown functionality
  savedCards?: SavedCard[];
  selectedCardId?: string | null;
  onCardSelect?: (cardId: string) => void;
  onAddNewCard?: () => void;
  theme?: ThemeConfig;
}

const SavedCardButton = React.forwardRef<HTMLDivElement, SavedCardButtonProps>(
  ({ 
    cardBrand, 
    cardLast4, 
    isLoading = false, 
    isDisabled = false, 
    onClick,
    savedCards,
    selectedCardId,
    onCardSelect,
    onAddNewCard,
    theme
  }, ref) => {
    const primaryColor = theme?.colors?.primary || '#49BDF3';
    const [showDropdown, setShowDropdown] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const selectedCardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

    // Scroll to selected card when dropdown opens
    useEffect(() => {
      if (showDropdown && selectedCardId && scrollContainerRef.current) {
        const selectedElement = selectedCardRefs.current.get(selectedCardId);
        if (selectedElement) {
          selectedElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      }
    }, [showDropdown, selectedCardId]);

    // Map Stripe card brands to icon types
    const getCardIcon = (brand: string) => {
      const brandLower = brand.toLowerCase(); 
      switch (brandLower) {
        case 'visa':
          return 'visa';
        case 'mastercard':
          return 'mastercard';
        case 'amex':
        case 'american_express':
          return 'amex';
        case 'discover':
          return 'discover';
        case 'diners':
        case 'diners_club':
          return 'diners';
        case 'unionpay':
          return 'unionpay';
        default:
          return 'card'; // fallback to generic card icon
      }
    };

    const hasMultipleCards = savedCards && savedCards.length >= 1;

    const handleDropdownToggle = (e: React.MouseEvent) => {
      e.stopPropagation();
      setShowDropdown(!showDropdown);
    };

    const handleCardSelection = (cardId: string) => {
      // Immediately apply the selection and close dropdown
      onCardSelect?.(cardId);
      setShowDropdown(false);
    };

    const handleMainButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // Only trigger onClick if not clicking on the dropdown toggle area
      if (hasMultipleCards) {
        const button = e.currentTarget;
        const rect = button.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const dividerPosition = rect.width - 42; // Approximate position of the divider
        
        // If clicked to the left of the divider, trigger the main action
        if (clickX < dividerPosition) {
          onClick?.(e);
        }
      } else {
        onClick?.(e);
      }
    };

    return (
      <div className="flex flex-col gap-[10px] relative" ref={ref}>
        {isLoading ? (
          <Button variant="disabled" disabled theme={theme}>
            <Loader2Icon className="animate-spin" />
            Please wait
          </Button>
        ) : (
          <>
            <Button
              variant={isDisabled ? "disabled" : "checkoutWithCard"}
              type={onClick ? "button" : "submit"}
              disabled={isDisabled}
              onClick={handleMainButtonClick}
              className="min-h-[48px] flex items-center justify-between gap-0 relative px-0 overflow-hidden"
              theme={theme}
            >
              <div className="flex items-center justify-center gap-2 flex-1">
                <span>Pay With</span>
                <Icons type={getCardIcon(cardBrand) as any} size={32} />
                <span>**** {cardLast4}</span>
              </div>
              {hasMultipleCards && (
                <>
                  <div className="absolute right-10 top-0 bottom-0 w-[1px] bg-white" />
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rotate-90 focus:outline-none px-4 h-full shrink-0"
                    onClick={handleDropdownToggle}
                    disabled={isDisabled}
                    aria-label="Toggle card dropdown"
                  >
                    <svg width="10" height="10" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M1.00148 1L7.27734 7L1.00148 13"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </>
              )}
            </Button>

            {/* Modal-style Dropdown */}
            {hasMultipleCards && showDropdown && (
              <>
                {/* Blurred backdrop for click outside */}
                <div 
                  className="fixed inset-0 z-40 backdrop-blur-xs"
                  onClick={() => setShowDropdown(false)}
                />
                
                {/* Modal */}
                <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[82%] max-w-[400px] bg-white shadow-2xl border-1 border-gray-300 rounded-sm overflow-hidden">
                  {/* Saved cards container - show 3 at a time */}
                  <div className="relative">
                    <div ref={scrollContainerRef} className="overflow-y-auto" style={{ maxHeight: 'calc(3 * 60px)' }}>
                      {savedCards.map((card, index) => (
                        <div key={card.id}>
                          <div
                            ref={(el) => {
                              if (el) {
                                selectedCardRefs.current.set(card.id, el);
                              } else {
                                selectedCardRefs.current.delete(card.id);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            className={`w-full text-left flex items-center justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                              selectedCardId === card.id ? 'bg-gray-100 border-t-1 border-b-1' : ''
                            } ${index === 0 && selectedCardId !== card.id ? `border-t border-gray-200` : ''
                            } ${index === savedCards.length - 1 && selectedCardId !== card.id ? `border-b border-gray-200` : ''}`}
                            style={selectedCardId === card.id ? { borderColor: primaryColor } : {}}
                            onClick={() => handleCardSelection(card.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleCardSelection(card.id);
                              }
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <div className="h-[29px] w-[40px] flex items-center justify-center">
                                <Icons
                                  type={(getCardIcon(card.card.brand) as IconType)}
                                  size={32}
                                />
                              </div>
                              <span className="text-[15px] text-[#212121] font-normal">
                                **** {card.card.last4}
                              </span>
                            </div>
                            {selectedCardId === card.id && (
                              <div className="flex items-center justify-center">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <circle cx="10" cy="10" r="10" fill={primaryColor}/>
                                  <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            )}
                          </div>
                          {index < savedCards.length - 1 && (
                            <div className="border-b border-gray-200" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Add New Card button */}
                  <div className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowDropdown(false);
                        onAddNewCard?.();
                      }}
                      className="w-full flex items-center justify-between px-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-[15px] text-[#212121] font-normal my-1">Add New Card</span>
                      <Icons type="chevronRight" size={14} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    );
  }
);

SavedCardButton.displayName = "SavedCardButton";

export default SavedCardButton;
